import React, { createContext, useEffect, useState, lazy, Suspense } from 'react';
import logo from './logo.svg';
import './App.css';

// <!-- Icon Font CSS -->
import './assets/css/plugins/icofont.min.css'
import './assets/css/plugins/flaticon.css';
import "./assets/css/plugins/font-awesome.min.css";

//<!-- Plugins CSS -->
import "./assets/css/plugins/animate.min.css";
import "./assets/css/plugins/swiper-bundle.min.css";
import "./assets/css/plugins/magnific-popup.css";
import "./assets/css/plugins/nice-select.css";
import "./assets/css/plugins/apexcharts.css";
import "./assets/css/plugins/jqvmap.min.css";

// <!-- Main Style CSS -->
import "./assets/css/style.css";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AnalyticsTracker from "./component/AnalyticsTracker";
import { pageRouteUrls } from './helper/pageRouteUrls';
// The landing page and the maintenance page stay eager: the home page is the first paint,
// and MaintenancePage renders before <Suspense>. Every other route is code-split (React.lazy)
// so it's fetched only when visited — this is what keeps the initial main.js bundle small.
import HomePage from './pages/home';
// Marketing SEO pages are imported eagerly (not lazy) so their copy + JSON-LD
// render synchronously and are captured by the react-snap prerender.
import ComparisonPage from './pages/marketing/ComparisonPage';
import CategoryLiveTeaching from './pages/marketing/CategoryLiveTeaching';
import MaintenancePage from './pages/maintenance';
import ApplicationContext from './contexts/ApplicationContext';
import CartContext from './contexts/CartContextProvider';
import CurrUserContext, { getCurrUserData, isAuthorized,getCurrUsrToken, setUserData } from './contexts/CurrUserContext';
import 'react-toastify/dist/ReactToastify.css';
import { SystemMessageModel } from './models/SystemMessageModel';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GoogleOAuthProvider } from "@react-oauth/google";
import CONFIG_KEYS from "./config";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CommonService } from './service/CommonService';
import { ToastContainer } from 'react-toastify';
import { useNotificationManager } from './contexts/useNotificationManager';
import { useShoppingManager } from './contexts/useShoppingManager';
import { ErrorProvider } from './contexts/ErrorContext';
import IsAuthRoute from './helper/IsAuthRoute';
import IsAdminRoute from './helper/IsAdminRoute';
import ScrollToTop from './component/ScrollToTop';
import { LoadingSpinner } from './component/LoadingSpinner';
import AiAssistantWidget from './component/AiAssistant/AiAssistantWidget';

// ── Code-split route pages ────────────────────────────────────────────────────
const LoginPage = lazy(() => import('./pages/login'));
const RegisterPage = lazy(() => import('./pages/register'));
const PricingPage = lazy(() => import('./pages/pricing'));
const CourseView = lazy(() => import('./pages/course/courses-view'));
const CourseOverview = lazy(() => import('./pages/course/course-overview'));
const CourseContent = lazy(() => import('./pages/course/course-content'));
const InstructorView = lazy(() => import('./pages/Instructor/instructor-view'));
const CourseSearch = lazy(() => import('./pages/search'));
const MyCourses = lazy(() => import('./pages/course/my-courses'));
const QuestionBankPage = lazy(() => import('./pages/questionbank/question-bank-page'));
const Messages = lazy(() => import('./pages/management/messages'));
const LectureView = lazy(() => import('./pages/lecture/lecture-view'));
const LectureInvoicesPage = lazy(() => import('./pages/lecture/lecture-invoices'));
const Error404 = lazy(() => import('./pages/error/error404'));
const Dashboard = lazy(() => import('./pages/dashboard/dashboard'));
const CourseEdit = lazy(() => import('./pages/course/course-edit'));
const LectureEdit = lazy(() => import('./pages/lecture/lecture-edit'));
const InstructorEdit = lazy(() => import('./pages/userSetting/instructor-edit'));
const MyStudentsPage = lazy(() => import('./pages/Instructor/my-students'));
const MyHomeworkPage = lazy(() => import('./pages/student/my-homework'));
const MyExamsPage = lazy(() => import('./pages/student/my-exams'));
const StudentInvoicesPage = lazy(() => import('./pages/Instructor/student-invoices'));
const UserEdit = lazy(() => import('./pages/userSetting/user-edit'));
const StudentEdit = lazy(() => import('./pages/userSetting/student-edit'));
const ParentEdit = lazy(() => import('./pages/userSetting/parent-edit'));
const VerifyEmailPage = lazy(() => import('./pages/verify-email'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password'));
const UserSettingsPage = lazy(() => import('./pages/management/user-settings'));
const ErrorNotAuth = lazy(() => import('./pages/error/errorNotAuth'));
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy'));
const HelpCenterPage = lazy(() => import('./pages/help/help-center'));
const HelpArticlePage = lazy(() => import('./pages/help/help-article'));
const TermsAndConditionsPage = lazy(() => import('./pages/terms-and-conditions'));
const ContactUsPage = lazy(() => import('./pages/contact-us'));
const AdminFeedbackPage = lazy(() => import('./pages/AdminPanel/feedback'));
const Error = lazy(() => import('./pages/error/error'));
const Billing = lazy(() => import('./pages/management/billing'));
const Checkout = lazy(() => import('./pages/checkout/checkout'));
const ProcessPayment = lazy(() => import('./pages/checkout/processPayment'));
const PaymentCanceled = lazy(() => import('./pages/checkout/paymentCanceled'));
const PublicInvoicePay = lazy(() => import('./pages/checkout/publicInvoicePay'));
const PublicCertificateView = lazy(() => import('./pages/certificate/publicCertificateView'));
const SubscriptionManagement = lazy(() => import('./pages/management/subscriptionManagement'));
const InstructorVerification = lazy(() => import('./pages/Instructor/instructor-verification'));
const InstructorVerificationList = lazy(() => import('./pages/Instructor/instructor-verification-list'));
const TutorFollow = lazy(() => import('./pages/Instructor/tutorFollow'));
const FavoriteLists = lazy(() => import('./pages/management/favorite-lists'));
const Payments = lazy(() => import('./pages/AdminPanel/payments'));
const AdminUsersListPage = lazy(() => import('./pages/AdminPanel/users-list'));
const AdminPromoCodesPage = lazy(() => import('./pages/AdminPanel/promo-codes'));
const AdminMarketingEmailsPage = lazy(() => import('./pages/AdminPanel/marketing-emails'));
const AdminMarketingWhatsAppPage = lazy(() => import('./pages/AdminPanel/marketing-whatsapp'));
const AdminCallsPage = lazy(() => import('./pages/AdminPanel/calls'));
const MyChildrenPage = lazy(() => import('./pages/childrenpage/mychildrenpage'));
const ParentReportsPage = lazy(() => import('./pages/childrenpage/parent-reports'));
const AcademyTutorsPage = lazy(() => import('./pages/Academy/academy-tutors'));
const AcademyView = lazy(() => import('./pages/Academy/academy-view'));
const AcademyReports = lazy(() => import('./pages/Academy/academy-reports'));
const StudentView = lazy(() => import('./pages/student/student-view'));
const MyCertificatesPage = lazy(() => import('./pages/certificate/my-certificates'));
const ChildrenCertificatesPage = lazy(() => import('./pages/certificate/children-certificates'));
const LectureLive = lazy(() => import('./pages/lecture/lecture-live'));

interface ErrorContextType {
  errorMessages: string[];
  setErrorMessages: (errors: string[]) => void;
}

function App() {
  const { cartItems, addToCart, removeFromCart, clearCart, isItemInCart } = useShoppingManager();


  const [message, setMessage] = useState<SystemMessageModel | null>(null);

  const setErrMessage = (msg: string) => {
    return setMessage({ error: msg });
  };
  const setSuccessMessage = (msg: string) => {
    return setMessage({ success: msg });
  };
  const setWarningMessage = (msg: string) => {
    return setMessage({ warning: msg });
  };
  const setInfoMessage = (msg: string) => {
    return setMessage({ info: msg });
  };


  const ErrorContext = createContext<ErrorContextType>({
    errorMessages: [],
    setErrorMessages: () => {},
  });


  return (
    <ApplicationContext.Provider value={{ message, setMessage, setErrMessage, setSuccessMessage, setWarningMessage, setInfoMessage }}>
      <ErrorProvider >
        <CurrUserContext.Provider value={{ setUserData, getCurrUserData,getCurrUsrToken, isAuthorized }}>
          <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, isItemInCart, clearCart }}>
            <BrowserRouter>
              <ScrollToTop />
              <AnalyticsTracker />
              <InnerApp />
            </BrowserRouter>
          </CartContext.Provider>
        </CurrUserContext.Provider>
        <ToastContainer />
      </ErrorProvider>
    </ApplicationContext.Provider>
  );
}

function InnerApp() {
  const { notifications, add, remove, setAsSeen } = useNotificationManager();

  // Site-wide maintenance gate, driven by the "FeatureFlags:IsLiveApp" flag in
  // appsettings.json. null = still checking (render nothing briefly to avoid a
  // flash of the real site); defaults to live if the check itself fails so an
  // API hiccup never takes the whole site down.
  const [isLiveApp, setIsLiveApp] = useState<boolean | null>(null);

  useEffect(() => {
    new CommonService().getIsLiveApp().then(setIsLiveApp);
  }, []);

  if (isLiveApp === null) return null;
  if (isLiveApp === false) return <MaintenancePage />;

  return (
      <GoogleOAuthProvider clientId={CONFIG_KEYS.googleClientId}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>

          <Route path={pageRouteUrls.Dashboard()} element={<IsAuthRoute><Dashboard /></IsAuthRoute>} />
          <Route path={pageRouteUrls.Home()} element={<HomePage />} />
          <Route path='/' element={<HomePage />} />
          <Route path={pageRouteUrls.Login()} element={<LoginPage />} />
          <Route path={pageRouteUrls.Register()} element={<RegisterPage />} />
          {/* Public pricing page — reachable by direct URL only; deliberately not in the nav bar. */}
          <Route path={pageRouteUrls.Pricing()} element={<PricingPage />} />

          {/* SEO marketing pages: /vs/:competitor comparisons + category landing page. */}
          <Route path={pageRouteUrls.VsCompetitor(":competitor")} element={<ComparisonPage />} />
          <Route path={pageRouteUrls.CheapestLiveTeaching()} element={<CategoryLiveTeaching />} />

          <Route path={pageRouteUrls.LectureView(":token")} element={<LectureView />} />
          <Route path={pageRouteUrls.LectureLiveSession(":token")} element={<IsAuthRoute><Suspense fallback={null}><LectureLive /></Suspense></IsAuthRoute>} />
          <Route path={pageRouteUrls.LectureEdit(":courseToken", ":token")} element={<IsAuthRoute><LectureEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.LectureAdd(":courseToken")} element={<IsAuthRoute><LectureEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.LectureInvoices(":token")} element={<IsAuthRoute><LectureInvoicesPage /></IsAuthRoute>} />

          {/* <Route path={pageRouteUrls.AccessManager()} element={<IsAuthRoute><AccessManager /></IsAuthRoute>} />
          <Route path={pageRouteUrls.LectureAccessManager(":lectureToken")} element={<IsAuthRoute><AccessManager /></IsAuthRoute>} />
          <Route path={pageRouteUrls.CourseAccessManager(":courseToken")} element={<IsAuthRoute><AccessManager /></IsAuthRoute>} /> */}
          
          <Route path={pageRouteUrls.Messages()} element={<IsAuthRoute><Messages /></IsAuthRoute>}/>

          <Route path={pageRouteUrls.CourseAdd()} element={<IsAuthRoute><CourseEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.CourseEdit(":token")} element={<IsAuthRoute><CourseEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.CourseView(":token")} element={<CourseView />} />
          <Route path={pageRouteUrls.CourseOverview(":token")} element={<CourseOverview />} />
          <Route path={pageRouteUrls.CourseContent(":token")} element={<CourseContent />} />
          <Route path={pageRouteUrls.Search()} element={<CourseSearch />} />
          <Route path={pageRouteUrls.CourseMy()} element={<IsAuthRoute><MyCourses /></IsAuthRoute>} />
          <Route path={pageRouteUrls.QuestionBank()} element={<IsAuthRoute><QuestionBankPage /></IsAuthRoute>} />

          <Route path={pageRouteUrls.Billing()} element={<IsAuthRoute><Billing /></IsAuthRoute>} />
          <Route path={pageRouteUrls.Checkout()} element={<IsAuthRoute><Checkout /></IsAuthRoute>} />
          <Route path={pageRouteUrls.ProcessPayment()} element={<ProcessPayment />} />
          <Route path={pageRouteUrls.PaymentCancele()} element={<PaymentCanceled />} />
          <Route path={pageRouteUrls.PublicInvoicePay(":invoiceToken")} element={<PublicInvoicePay />} />
          <Route path={pageRouteUrls.PublicCertificateView(":certificateToken")} element={<PublicCertificateView />} />
          <Route path={pageRouteUrls.SubscriptionManagement()} element={<IsAuthRoute><SubscriptionManagement /></IsAuthRoute>} />

          <Route path={pageRouteUrls.Payments()} element={<IsAuthRoute><Payments /></IsAuthRoute>} />
          <Route path={pageRouteUrls.AdminUsersList()} element={<IsAdminRoute><AdminUsersListPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.AdminPromoCodes()} element={<IsAdminRoute><AdminPromoCodesPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.AdminFeedback()} element={<IsAdminRoute><AdminFeedbackPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.AdminMarketingEmails()} element={<IsAdminRoute><AdminMarketingEmailsPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.AdminMarketingWhatsApp()} element={<IsAdminRoute><AdminMarketingWhatsAppPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.AdminCalls()} element={<IsAdminRoute><AdminCallsPage /></IsAdminRoute>} />
          <Route path={pageRouteUrls.InstructorView(":token")} element={<InstructorView />} />
          <Route path={pageRouteUrls.InstructorEdit()} element={<IsAuthRoute><InstructorEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.InstructorVerification()} element={<IsAuthRoute><InstructorVerification /></IsAuthRoute>} />
          <Route path={pageRouteUrls.InstructorVerificationList()} element={<IsAdminRoute><InstructorVerificationList /></IsAdminRoute>} />
          <Route path={pageRouteUrls.InstructorFollowList()} element={<IsAuthRoute><TutorFollow /></IsAuthRoute>} />
          <Route path={pageRouteUrls.MyStudents()} element={<IsAuthRoute><MyStudentsPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.MyHomework()} element={<IsAuthRoute><MyHomeworkPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.MyExams()} element={<IsAuthRoute><MyExamsPage /></IsAuthRoute>} />

          <Route path={pageRouteUrls.StudentInvoices()} element={<IsAuthRoute><StudentInvoicesPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.FavoriteLists()} element={<IsAuthRoute><FavoriteLists /></IsAuthRoute>} />
           
          <Route path={pageRouteUrls.MyChildrenPage()} element={<IsAuthRoute><MyChildrenPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.ParentReports()} element={<IsAuthRoute><ParentReportsPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.MyCertificates()} element={<IsAuthRoute><MyCertificatesPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.ChildrenCertificates()} element={<IsAuthRoute><ChildrenCertificatesPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.AcademyTutors()} element={<IsAuthRoute><AcademyTutorsPage /></IsAuthRoute>} />
          <Route path={pageRouteUrls.AcademyView(":token")} element={<AcademyView />} />
          <Route path={pageRouteUrls.AcademyReports()} element={<IsAuthRoute><AcademyReports /></IsAuthRoute>} />
          <Route path={pageRouteUrls.StudentView(":token")} element={<StudentView />} />
 
          <Route path={pageRouteUrls.UserEdit()} element={<IsAuthRoute><UserEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.StudentEdit()} element={<IsAuthRoute><StudentEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.ParentEdit()} element={<IsAuthRoute><ParentEdit /></IsAuthRoute>} />
          <Route path={pageRouteUrls.VerifyEmail()} element={<VerifyEmailPage />} />
          <Route path={pageRouteUrls.ForgotPassword()} element={<ForgotPasswordPage />} />
          <Route path={pageRouteUrls.ResetPassword()} element={<ResetPasswordPage />} />
          <Route path={pageRouteUrls.UserSettings()} element={<IsAuthRoute><UserSettingsPage /></IsAuthRoute>} />

          <Route path={pageRouteUrls.ErrorNotAuth()} element={<ErrorNotAuth />} />
          <Route path={pageRouteUrls.HelpCenter()} element={<HelpCenterPage />} />
          <Route path={pageRouteUrls.HelpArticle(":slug")} element={<HelpArticlePage />} />
          <Route path={pageRouteUrls.PrivacyPolicy()} element={<PrivacyPolicyPage />} />
          <Route path={pageRouteUrls.TermsAndConditions()} element={<TermsAndConditionsPage />} />
          <Route path={pageRouteUrls.ContactUs()} element={<ContactUsPage />} />
          <Route path={pageRouteUrls.Error(":code")} element={<Error />} />
          <Route path='*' element={<Error404 />} />

          {/* <Route path='signin-oidc' element={<HomePage />} /> */}
        </Routes>
        </Suspense>
        {/* In-app help assistant — mounted once globally so it shows on every page,
            for logged-in users and anonymous visitors alike. */}
        <AiAssistantWidget />
        {/* <ScrollToTop /> */}
      </LocalizationProvider>
      </GoogleOAuthProvider>
  );
}

export default App;


